import { Avatar, Button, ListItem, Stack, Typography } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { ToggleableChip } from './ToggleableChip';
import { Dispatch, useCallback, useLayoutEffect, useState } from 'react';
import { PurpleX } from 'Images';
import { useDictionary, useIsDesktop } from 'Hooks';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';
import { RevealFiltersButton } from './RevealFiltersButton';

// Rough estimates
const containerMaxWidth = 850;

export interface IFilterTag {
  label: string;
  isActive: boolean;
}

interface IDocumentsFilterTags {
  reset: () => void;
  subjects: string[];
  selectedSubjects: string[];
  setSelectedSubjects: Dispatch<React.SetStateAction<string[]>>;
  clearSelectedSubjects: () => void;
}

export const DocumentsFilterTags = ({
  subjects,
  selectedSubjects,
  setSelectedSubjects,
  reset,
  clearSelectedSubjects,
}: IDocumentsFilterTags) => {
  const [isMoreThan1Line, setIsMoreThan1Line] = useState(false);
  const [isTagsRevealed, setIsTagsRevealed] = useState(false);
  const dictionary = useDictionary(Dictionary, DICTIONARY.Documents);
  const isDesktop = useIsDesktop();

  const getTextWidth = useCallback((text: string) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    let width = 0;
    const minWidth = 64;

    if (context) {
      context.font = getComputedStyle(document.body).font;
      width = context.measureText(text).width;
    }

    canvas.remove();
    if (width < minWidth) return minWidth;
    return width;
  }, []);

  const toggleSelectedSubject = (subject: string) => {
    setSelectedSubjects((prevSelectedSubjects) => {
      if (prevSelectedSubjects.includes(subject)) {
        return prevSelectedSubjects.filter(
          (selectedSubject) => selectedSubject !== subject
        );
      } else {
        return [...prevSelectedSubjects, subject];
      }
    });
  };

  const isChipActive = useCallback(
    (title: string): boolean => {
      return selectedSubjects.includes(title);
    },
    [selectedSubjects]
  );

  useLayoutEffect(() => {
    const tagWidths: number[] = subjects.map(getTextWidth);
    const tagWidthsSum = tagWidths.reduce((sum, width) => (sum += width), 0);
    const isOverflowing = tagWidthsSum > containerMaxWidth;
    if (!isDesktop) {
      const mobileContainerDeviationWidth = 100;
      const isOverflowing =
        tagWidthsSum > window.innerWidth - mobileContainerDeviationWidth;
      setIsMoreThan1Line(isOverflowing);
      return;
    }

    setIsMoreThan1Line(isOverflowing);
  }, [getTextWidth, isMoreThan1Line, subjects, isDesktop]);

  const expandFilterTags = () => {
    setIsTagsRevealed(true);
  };

  const collapseFilterTags = () => {
    setIsTagsRevealed(false);
    clearSelectedSubjects();
  };

  return (
    <Stack gap={isDesktop ? '24px' : '16px'}>
      <Typography variant='body1' color='text.secondary' component='h3'>
        {dictionary.filterTitle}
      </Typography>
      <Stack direction={'column'} gap={isDesktop ? '24px' : '16px'}>
        <Stack direction={'row'} maxWidth={'920px'}>
          <Stack
            direction={'row'}
            component={'ul'}
            padding={0}
            margin={0}
            gap={'8px'}
            overflow={'hidden'}
            height={isTagsRevealed ? 'auto' : '40px'}
            flexWrap={'wrap'}
          >
            {subjects.map((title: string, index: number) => (
              <ListItem
                key={index}
                sx={{
                  cursor: 'pointer',
                  display: 'inline',
                  width: 'auto',
                  border: '1px solid #E7EDFF',
                  borderRadius: '48px',
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  '& > button': {
                    outline: 'none',
                    height: 'auto',
                  },
                }}
                value={title}
              >
                <ToggleableChip
                  chip={{ label: title, isActive: isChipActive(title) }}
                  onClick={toggleSelectedSubject}
                />
              </ListItem>
            ))}
            {isMoreThan1Line && isTagsRevealed && (
              <RevealFiltersButton
                isExpanded={true}
                onClick={() => collapseFilterTags()}
              />
            )}
          </Stack>
          {isMoreThan1Line && !isTagsRevealed && (
            <RevealFiltersButton
              isExpanded={false}
              onClick={() => expandFilterTags()}
            />
          )}
        </Stack>
        {selectedSubjects.length > 0 && (
          <Button
            onClick={reset}
            onKeyDown={onEnterOrEscapeKeyDown}
            sx={{
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'flex-start',
              '&:hover': {
                background: 'transparent',
              },
            }}
            tabIndex={0}
          >
            <Avatar
              sx={{ width: '22px', height: '22px' }}
              src={PurpleX}
              alt={dictionary.filterReset}
            />
            <Typography variant='body2'>{dictionary.filterReset}</Typography>
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
